import { useClerk } from '@clerk/react';
import { useEffect, useRef } from 'react';

// A Clerk sign-in ticket arriving as `__clerk_ticket` in the URL, captured at
// module load: RequireAuth bounces signed-out visitors to `/` with a history
// replace before any effect can read the query, so the capture must happen
// before React renders at all. Security lives entirely in the ticket: it is
// minted server-side with the instance's secret key, single use, and
// short-lived, which is the same trust Clerk's own invitation links carry.
const { ticketAtLoad, destinationAtLoad } = (() => {
  const params = new URLSearchParams(window.location.search);
  const ticket = params.get('__clerk_ticket');
  if (!ticket) return { ticketAtLoad: null, destinationAtLoad: null };
  params.delete('__clerk_ticket');
  const query = params.toString();
  const destination = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  window.history.replaceState(null, '', destination);
  return { ticketAtLoad: ticket, destinationAtLoad: destination };
})();

// Completes a ticket sign-in when the page loaded with one. Renders nothing;
// mounted once inside ClerkProvider. A consumed, expired, or garbage ticket
// resolves to a failed sign-in attempt and the page stays signed out,
// which is the same terminal state as not having a ticket at all.
export default function TicketSignIn() {
  const clerk = useClerk();
  const attempted = useRef(false);

  useEffect(() => {
    if (!ticketAtLoad || attempted.current || !clerk.loaded) return;
    attempted.current = true;
    void (async () => {
      try {
        const attempt = await clerk.client.signIn.create({
          strategy: 'ticket',
          ticket: ticketAtLoad,
        });
        if (attempt.status === 'complete') {
          await clerk.setActive({ session: attempt.createdSessionId });
          // RequireAuth replaces a protected route with `/` before the async
          // ticket attempt resolves, so a ticket opened on /superuser/* would
          // otherwise land its signed-in visitor on the homepage. A full
          // navigation (not router state) also clears the sign-in modal the
          // guard opened while the attempt was in flight.
          const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
          if (destinationAtLoad && destinationAtLoad !== current) {
            window.location.assign(destinationAtLoad);
          }
        }
      } catch {
        // Invalid or already-consumed ticket: remain signed out.
      }
    })();
  }, [clerk, clerk.loaded, clerk.user]);

  return null;
}
